/**
 * Fase 5 — Kurs-veiviser steg 2: Tidsplan.
 * Kurs-datoer, antall plasser, eksamensdato og klokkeslett.
 */
import { useEffect } from 'react';
import { Calendar, Users, Clock, Info } from 'lucide-react';
import type { CourseWizardData } from '../NewCourseInstanceWizard';

export interface CourseScheduleStepProps {
  data: CourseWizardData;
  patch: (updates: Partial<CourseWizardData>) => void;
  /** Hentes fra prøvesett for å utlede eksamensslutt */
  examDurationMinutes?: number;
}

export default function CourseScheduleStep({ data, patch, examDurationMinutes }: CourseScheduleStepProps) {
  // Avgjør hvilke tidsplan-bokser som skal vises:
  // - Skriftlig prøve har tidsstyrt SEB-vindu (dato + starttid + varighet)
  // - Andre vurderingsformer har egen frist per komponent
  const hasWrittenComponent = data.component_deadlines.some(c => c.component_type === 'written');
  const hasNonWrittenComponents = data.component_deadlines.some(c => c.component_type !== 'written');

  // Auto-forhåndsutfyll eksamensdato med kursslutt (eller kursstart) så kalenderen
  // åpner på riktig måned. Gjelder KUN prøvesett med skriftlig prøve — ellers
  // trenger vi ikke exam_date i det hele tatt (prosjekt/portfolio/muntlig bruker
  // komponent-frister istedenfor). Brukeren kan overstyre uten friksjon.
  useEffect(() => {
    if (hasWrittenComponent && !data.exam_date && (data.end_date || data.start_date)) {
      const defaultDate = data.end_date || data.start_date;
      patch({ exam_date: defaultDate });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.end_date, data.start_date, hasWrittenComponent]);

  // Utled eksamensslutt
  let examEnd: string | null = null;
  if (data.exam_date && data.exam_time && examDurationMinutes) {
    try {
      const start = new Date(`${data.exam_date}T${data.exam_time}:00`);
      const end = new Date(start.getTime() + examDurationMinutes * 60_000);
      examEnd = end.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' });
    } catch { /* ignore */ }
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-kf-gray-700 mb-1">
            <Calendar size={14} className="inline mr-1" />
            Kursstart <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            value={data.start_date}
            onChange={(e) => patch({ start_date: e.target.value })}
            className="w-full px-3 py-2 border border-kf-gray-400 rounded-lg shadow-sm bg-white focus:ring-2 focus:ring-kf-blue-500 focus:border-kf-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-kf-gray-700 mb-1">
            <Calendar size={14} className="inline mr-1" />
            Kursslutt
          </label>
          <input
            type="date"
            value={data.end_date}
            onChange={(e) => patch({ end_date: e.target.value })}
            min={data.start_date || undefined}
            className="w-full px-3 py-2 border border-kf-gray-400 rounded-lg shadow-sm bg-white focus:ring-2 focus:ring-kf-blue-500 focus:border-kf-blue-500"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-kf-gray-700 mb-1">
          <Users size={14} className="inline mr-1" />
          Antall plasser (planlagt)
        </label>
        <input
          type="number"
          value={data.max_candidates ?? ''}
          onChange={(e) => patch({ max_candidates: e.target.value ? parseInt(e.target.value) : null })}
          placeholder="Ubegrenset"
          min={1}
          className="w-40 px-3 py-2 border border-kf-gray-300 rounded-lg focus:ring-2 focus:ring-kf-blue-500 focus:border-kf-blue-500"
        />
        <span className="text-xs text-kf-gray-500 ml-2">
          Når antallet er nådd havner nye påmeldinger på venteliste.
        </span>
      </div>

      {/* Eksamenstidspunkt — vises KUN for prøvesett som har skriftlig prøve (SEB-vindu).
          Prosjekt/portfolio/muntlig osv. bruker frister-boksen under istedenfor. */}
      {hasWrittenComponent && (
        <div className="rounded-lg border-2 border-kf-blue-300 bg-kf-blue-50/40 p-4 shadow-sm">
          <h4 className="font-semibold text-kf-gray-900 mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4 text-kf-blue-600" />
            Eksamenstidspunkt <span className="text-xs font-normal text-kf-gray-500">(skriftlig prøve)</span>
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-kf-gray-700 mb-1">Dato</label>
              <input
                type="date"
                value={data.exam_date}
                onChange={(e) => patch({ exam_date: e.target.value })}
                min={data.start_date || undefined}
                className="w-full px-3 py-2 text-sm border border-kf-gray-400 rounded-lg bg-white shadow-sm focus:ring-2 focus:ring-kf-blue-500 focus:border-kf-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-kf-gray-700 mb-1">Starttid</label>
              <input
                type="time"
                value={data.exam_time}
                onChange={(e) => patch({ exam_time: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-kf-gray-400 rounded-lg bg-white shadow-sm focus:ring-2 focus:ring-kf-blue-500 focus:border-kf-blue-500"
              />
            </div>
          </div>
          {examEnd && (
            <p className="text-xs text-kf-blue-700 mt-2 flex items-center gap-1">
              <Info className="w-3 h-3" />
              Eksamensslutt beregnes automatisk til <strong>{examEnd}</strong> basert på prøvesettets varighet
              ({examDurationMinutes} min).
            </p>
          )}
          {!examDurationMinutes && data.exam_date && (
            <p className="text-xs text-amber-700 mt-2">
              Varighet ikke satt på prøvesettet. Eksamensslutt beregnes ved lagring.
            </p>
          )}
        </div>
      )}

      {/* Per-komponent-frister for ikke-skriftlige vurderingsformer.
          Vises alene (prosjekt-prøvesett) eller i tillegg til Eksamenstidspunkt (kompositt). */}
      {hasNonWrittenComponents && (
        <div className="rounded-lg border-2 border-purple-300 bg-purple-50/40 p-4 shadow-sm">
          <h4 className="font-semibold text-kf-gray-900 mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4 text-purple-700" />
            {hasWrittenComponent ? 'Frister for andre vurderingsformer' : 'Frister for vurderingsformer'}
          </h4>
          <p className="text-xs text-kf-gray-600 mb-3">
            {hasWrittenComponent
              ? 'Dette prøvesettet har komponenter i tillegg til skriftlig prøve. Hver av disse har egen frist for innlevering eller gjennomføring. Du kan overstyre standarden (kursslutt) nedenfor.'
              : 'Dette prøvesettet har ingen skriftlig prøve. Sett frister for innlevering eller gjennomføring nedenfor. Tomt felt = kursslutt.'}
          </p>
          <div className="space-y-3">
            {data.component_deadlines.map((comp, idx) => {
              if (comp.component_type === 'written') return null;
              const label = (
                comp.component_type === 'project' ? 'Prosjektoppgave — innleveringsfrist' :
                comp.component_type === 'portfolio' ? 'Mappevurdering — innleveringsfrist' :
                comp.component_type === 'group' ? 'Gruppearbeid — innleveringsfrist' :
                comp.component_type === 'oral' ? 'Muntlig eksamen — gjennomføringsdato' :
                comp.component_type === 'practical' ? 'Praktisk demonstrasjon — gjennomføringsdato' :
                comp.component_type === 'observation' ? 'Kontinuerlig observasjon — vurderingsdato' :
                `${comp.component_type} — dato`
              );
              const isInnlevering = ['project', 'portfolio', 'group'].includes(comp.component_type);
              // Konverter end_date (YYYY-MM-DD) til datetime-local-format ved placeholder
              const defaultValue = data.end_date ? `${data.end_date}T23:59` : '';
              return (
                <div key={`${comp.component_type}-${idx}`} className="bg-white border border-kf-gray-300 rounded p-3 shadow-sm">
                  <label className="block text-sm font-medium text-kf-gray-800 mb-1">
                    {label}
                  </label>
                  {comp.task_description && (
                    <p className="text-xs text-kf-gray-500 italic mb-2">{comp.task_description}</p>
                  )}
                  <input
                    type="datetime-local"
                    value={comp.deadline}
                    onChange={e => {
                      const next = data.component_deadlines.map((c, i) =>
                        i === idx ? { ...c, deadline: e.target.value } : c
                      );
                      patch({ component_deadlines: next });
                    }}
                    min={data.start_date ? `${data.start_date}T00:00` : undefined}
                    placeholder={defaultValue}
                    className="w-full px-3 py-2 text-sm border border-kf-gray-400 rounded-lg bg-white shadow-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                  <p className="text-xs text-kf-gray-500 mt-1">
                    {comp.deadline
                      ? (isInnlevering
                        ? 'Kandidat kan laste opp innlevering frem til dette tidspunktet (pluss evt. grace-buffer).'
                        : 'Sensor må registrere vurdering innen dette tidspunktet.')
                      : `Tom = ${isInnlevering ? 'innleveringsfrist' : 'vurderingsdato'} settes til kursslutt (${data.end_date || 'ikke valgt'}).`}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
